import { ImageName } from "@mcleod/images";
import { CommonDialogs, UserLayoutProvider, YesNoDialogProps } from "@mcleod/common";
import {
    Button, ButtonVariant, ClickEvent, Component, CrudDecorator, DataSourceMode, DataSourceRemoteDataChangeEvent, Event,
    HorizontalSpacer, Label, Layout, LookupModelSelectionEvent, ModelDataChangeType, Overlay, Panel, Snackbar, Tab,
    Table, TableRow, Toast
} from "@mcleod/components";
import {
    ActionPermission, Alignment, Api, ArrayUtil, HorizontalAlignment, LogManager, ModelRow, Navigation, PermissionsUtil,
    StringUtil, UserSettings
} from "@mcleod/core";
import { LMEUserSettings } from "@mcleod/general";
import { ModelOrders, OrderHandlingRequirementsSlideout, OrderTabView } from "@mcleod/dispatch";
import { CompareTender } from "./CompareTender";
import { DeclineTender } from "./DeclineTender";
import { LinkTenderDialog } from "./LinkTenderDialog";
import { LoadTenderExpressProfileFilter } from "./LoadTenderExpressProfileFilter";
import { ReplyResults } from "./ReplyResults";
import { TenderGridLayout } from "./TenderGridLayout";
import { AutogenLayoutLoadTenderExpress } from "./autogen/AutogenLayoutLoadTenderExpress";
import { ModelEdiLtxexpressProf, RowEdiLtxexpressProf } from "./models/ModelEdiLtxexpressProf";
import { ModelEdiOrder, RowEdiOrder } from "./models/ModelEdiOrder";
import { ModelEdiUser } from "./models/ModelEdiUser";
import { ModelEdiorderProfile, RowEdiorderProfile } from "./models/ModelEdiorderProfile";
import { MakeOrderUtility } from "./MakeOrderUtility";
import { ModelOtherChargeEdi } from "./models/ModelOtherChargeEdi";
import { LoadTenderExpressFilter } from "./LoadTenderExpressFilter";
import { EdiUtility } from "./EdiUtility";

const log = LogManager.getLogger("lme.datafusion.LoadTenderExpress");
const dateTimeOptions = { month: "2-digit", day: "2-digit", year: "2-digit", hour: "numeric", minute: "numeric", hour12: false } as const;

export class LoadTenderExpress extends AutogenLayoutLoadTenderExpress {
    public selectedRow: TableRow;
    public selectedRows: TableRow[];
    private _ltxProfileId: string;
    private _refreshAllowed: boolean;
    private _interval: number;
    private _intervalId: number;
    private _ltxProfile: RowEdiLtxexpressProf;
    private _isFilterSet: boolean;
    private _expressMode: string;
    private _showResultPanel: any;
    private _showNewestFirst: string;
    private _readyForNext: boolean;
    private _waitingOnInput: boolean;
    private filterObject: LoadTenderExpressFilter;

    // Caching infrastructure
    private _profileCache: Map<string, RowEdiLtxexpressProf> = new Map();
    private _ediOrderCache: Map<string, RowEdiOrder> = new Map();
    private _ediOrderProfileCache: Map<string, RowEdiorderProfile> = new Map();
    private _orderStatusCache: Map<string, { status: string, timestamp: number }> = new Map();
    private _userSettingsCache: any = null; // Cache for user settings
    private readonly CACHE_TTL_MS = 30000; // 30 seconds TTL for order status

    get ltxProfileId(): String {
        return this._ltxProfileId
    }

    override async onLoad(): Promise<void> {
        (this.layoutAll as TenderGridLayout).LoadTenderExpress = this;
        (this.layoutOriginal as TenderGridLayout).LoadTenderExpress = this;
        (this.layoutChange as TenderGridLayout).LoadTenderExpress = this;
        (this.layoutCancel as TenderGridLayout).LoadTenderExpress = this;

        this._refreshAllowed = true;
        this._interval = 0;
        await this.defaultProfileId();
        this.disableReplyOptions();

        if (!StringUtil.isEmptyString(this.ltxProfileId)) {
            await this.searchMainDatasource(this._isFilterSet);
            this.updateRefreshLabel();
        }
        this.ltxProfileTextbox.addBeforeLookupModelSearchListener(event => {
            if (this._ltxProfile != null && this._ltxProfile.get("id", "") === this.ltxProfileTextbox.text) {
                event.filter = null;
            }
        });

        this.ltxProfileTextbox.addLookupModelSelectionListener(async (event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true) {
                if (this.ltxProfileTextbox.getFirstLookupModelData()?.get("id") != this._ltxProfile?.get("id") || this._isFilterSet) {
                    this._ltxProfileId = event.target.lookupModelData[0].data.id;
                    if (this._ltxProfileId != null) {
                        await this.lookupLtxProfileRow(this._ltxProfileId);
                    }
                    this._isFilterSet = false;
                    this.setLtxTitle(this._ltxProfileId);
                    this.refreshScreen();
                    // Instead of new query, apply profile as client-side filter
                    this.applyProfileFilter();
                    this.buttonFilter.imageName = ImageName.profileFilter;
                    this.disableReplyOptions();
                    this.findSelectedProfile();
                }
            }
        });

        Navigation.addNavigationListener({
            onNavigate: () => {
                if (this._intervalId) {
                    clearInterval(this._intervalId);
                }
                (this.layoutAll as TenderGridLayout).clearClockTimer();
                (this.layoutOriginal as TenderGridLayout).clearClockTimer();
                (this.layoutChange as TenderGridLayout).clearClockTimer();
                (this.layoutCancel as TenderGridLayout).clearClockTimer();
            },
            persistAcrossPages: false
        })
    }

    buttonFilterOnClick(event: ClickEvent) {
        Layout.getLayout("lme/datafusion/LoadTenderExpressProfileFilter", {
            backgroundColor: "defaultBackground", borderShadow: true, paddingBottom: 50, fillHeight: false, width: 1600
        }).addLayoutLoadListener(event => {
            this.showFilter(event.target as LoadTenderExpressProfileFilter);
        });
    }

    private showFilter(filterLayout: LoadTenderExpressProfileFilter) {
        filterLayout.mainDataSource.createBlankRow().then(row => {
            row._appending = false;
            row.setValues(this._ltxProfile.data);
            filterLayout.mainDataSource.data = [row];
            filterLayout.mainDataSource.setRowIndexWithoutUpdate(0);
            filterLayout.mainDataSource.mode = DataSourceMode.UPDATE;
            filterLayout.buttonCancel.addClickListener(() => Overlay.hideOverlay(overlay));
            filterLayout.buttonApply.addClickListener(async () => {
                if (filterLayout.validateSimple()) {
                    Overlay.hideOverlay(overlay);
                    // Always apply filter when user clicks Apply - don't rely on hasChanged()
                    // since change tracking can be unreliable after row is set up via setValues()
                    this.disableReplyOptions();
                    this._isFilterSet = true;
                    this.resetToPrevious(row);
                    await this.setAdvancedLtxProfileRowFilter(row as RowEdiLtxexpressProf, true);
                }
            });

            // Apply scrolling styles to the nested profile layout, not the top-level filter layout
            const profileLayout = filterLayout.layoutProfile;
            if (profileLayout) {
                profileLayout.style.overflowY = "auto";
                profileLayout.style.overflowX = "hidden";
                profileLayout.style.maxHeight = "calc(85vh - 80px)"; // Account for header panel height
            }

            // Set overall constraints on the filter layout and use flexbox for proper layout
            filterLayout.style.maxHeight = "85vh";
            filterLayout.style.maxWidth = "90vw";
            filterLayout.style.display = "flex";
            filterLayout.style.flexDirection = "column";

            const overlay = Overlay.showInOverlay(filterLayout, {
                closeOnClickOff: false,
                greyedBackground: true,
                centered: true
            });

            // Force scrolling with direct DOM manipulation after overlay is shown
            setTimeout(() => {
                try {
                    // Get the overlay content element (the one that wraps our filterLayout)
                    const overlayContent = overlay.getOverlayContent();
                    if (overlayContent && overlayContent._element) {
                        // Use responsive height that shows full layout at 100% zoom
                        overlayContent._element.style.height = '85vh';
                        overlayContent._element.style.maxHeight = '85vh';
                        overlayContent._element.style.overflowY = 'auto'; // Only show scrollbar when needed
                        overlayContent._element.style.overflowX = 'hidden';
                    }

                    // RESTORE: Apply to filterLayout too (this was working!)
                    if (filterLayout._element) {
                        filterLayout._element.style.height = '85vh'; // Responsive height
                        filterLayout._element.style.maxHeight = '85vh';
                        filterLayout._element.style.overflowY = 'auto'; // Only show scrollbar when needed
                        filterLayout._element.style.overflowX = 'hidden';
                    }
                } catch (error) {
                    console.error('Error applying scrolling:', error);
                }
            }, 200);
        });
    }

    public async setAdvancedLtxProfileRowFilter(value: RowEdiLtxexpressProf, profileFiltered: boolean = false) {
        // When profileFiltered is true (user modified filter on-the-fly), always reapply
        // Otherwise, only update if the profile row reference changed
        if (profileFiltered || value !== this._ltxProfile) {
            this.setLtxProfileRow(value);
            this.buttonFilter.visible = this._ltxProfile !== null;
            if (this._ltxProfile != null) {
                await this.setLtxTitle();
                // Re-query server with updated filter settings
                await this.searchMainDatasource(true);
                this.ltxProfileTextbox.text = profileFiltered ? "" : this._ltxProfile.get("id");
                this.buttonFilter.imageName = profileFiltered ? ImageName.profileFiltered : ImageName.profileFilter;
            }
        }
    }

    public async searchMainDatasource(searchByFilter = false) {
        this.tabsetTenderExpress.components.forEach((tab: Component) => ((tab as Tab).findComponentById("tableLtx") as Table).busy = true);
        await this.mainDataSource.search(searchByFilter ? { profile_row: this._ltxProfile } : { id: this.ltxProfileId });
        this.tabsetTenderExpress.components.forEach((tab: Component) => {
            ((tab as Tab).findComponentById("tableLtx") as Table).busy = false;
        });
    }

    /**
     * Apply profile filtering to existing cached data instead of querying server
     */
    private applyProfileFilter() {
        if (!this.filterObject || !this.mainDataSource.data) {
            return;
        }

        // Filter existing data using client-side filtering
        const filteredData = this.mainDataSource.data.filter(row => this.filterObject.dataPasses(row));

        // Update all tab layouts with filtered data
        this.layoutAll.mainDataSource.data = filteredData;
        this.layoutAll.mainDataSource.displayDataInBoundComponents();
        this.setCaption(this.tabAll, this.layoutAll);

        this.layoutOriginal.mainDataSource.data = filteredData.filter(tender => tender.data["is_original"]);
        this.setCaption(this.tabOriginal, this.layoutOriginal);
        this.layoutOriginal.mainDataSource.displayDataInBoundComponents();

        this.layoutChange.mainDataSource.data = filteredData.filter(tender => tender.data["is_change"]);
        this.setCaption(this.tabChange, this.layoutChange);
        this.layoutChange.mainDataSource.displayDataInBoundComponents();

        this.layoutCancel.mainDataSource.data = filteredData.filter(tender => tender.data["is_cancel"]);
        this.setCaption(this.tabCancel, this.layoutCancel);
        this.layoutCancel.mainDataSource.displayDataInBoundComponents();
    }

    private resetToPrevious(row: ModelRow) {
        this.setModelRowField("dest_zones", row);
        this.setModelRowField("orig_zones", row);
        this.setModelRowField("customer_ids", row);
        this.setModelRowField("partner_ids", row);
        this.setModelRowField("revenue_codes", row);
    }

    private setModelRowField(field: string, row: ModelRow<any>) {
        const currentValue = row?.get(field);
        const originalValue = this._ltxProfile?.data[field]
        if (currentValue !== originalValue) {
            if (!currentValue) {
                row.set(field, "*");
            }
        }
    }

    private async getLoadTenderExpressProfile(value: string): Promise<RowEdiLtxexpressProf | undefined> {
        // Check cache first
        if (this._profileCache.has(value)) {
            return this._profileCache.get(value);
        }

        // Fetch from server and cache result
        const profile = await new ModelEdiLtxexpressProf().searchSingle({ id: value }) as RowEdiLtxexpressProf;
        if (profile) {
            this._profileCache.set(value, profile);
        }
        return profile;
    }

    async acceptTenders() {
        const selectedRows = this.selectedRows;
        if (ArrayUtil.isEmptyArray(selectedRows)) {
            Snackbar.showWarningSnackbar("No rows selected.  Please select a row before accepting.")
            this.disableReplyOptions();
            return;
        }
        this.disableReplyOptions();
        let refreshStopped = false;
        let origTenderList: string = "";
        this.buttonAccept.busy = true;

        if (this._refreshAllowed) {
            log.info("Refresh currently active.  Stopping refresh.");
            refreshStopped = true;
            clearInterval(this._intervalId);
        }

        const processingSingleRow: boolean = selectedRows.length == 1 ? true : false;

        if (this._expressMode == "Y") {
            log.info("Processing " + selectedRows.length + " tenders using express mode.");

            for (let x = 0; x < selectedRows.length; x++) {
                const tender = selectedRows[x];
                const ediOrderId: string = tender.data.get("tender_id");
                let orderId: string = tender.data.get("order_id");
                const changeReplyReq = tender.data.get("requires_reply_change");
                const cancelReplyReq = tender.data.get("requires_reply_cancel");
                const direction = tender.data.get("direction");
                const ediOrderRow = await this.findEdiOrderRow(ediOrderId);
                if ("Y" == ediOrderRow.get("record_in_use")) {
                    Snackbar.showSnackbar("Shipment ID: " + ediOrderRow.get("shipment_id") + ". This tender is currently locked, and can not be accepted.", { persist: true });
                    this.buttonAccept.busy = false;
                    return;
                }

                if ("N" == ediOrderRow.get("ignore_map_errors") && 0 < ediOrderRow.get("error_count")[0]) {
                    Snackbar.showWarningSnackbar("Load tender " + ediOrderId + " has errors that must be reviewed prior to order creation.");
                    this.buttonAccept.busy = false;
                    return;
                }

                if (await this.hasInvalidChargeCodes(ediOrderId, direction)) {
                    Snackbar.showWarningSnackbar("Load tender " + ediOrderId + " has invalid/missing other charge codes which must be corrected before creating an order. " +
                        "Please use the View Load Tender screen to correct/process this tender.");
                    this.buttonAccept.busy = false;
                    return;
                }

                if (tender.data.get("is_original")) {
                    if (!StringUtil.isEmptyString(origTenderList)) {
                        origTenderList += ",";
                    }
                    origTenderList += ediOrderId;
                }
                else if (tender.data.get("is_change")) {
                    if (processingSingleRow) {
                        if (StringUtil.isEmptyString(orderId)) {
                            orderId = await this.findOriginalOrderId(ediOrderRow);
                        }

                        if (!StringUtil.isEmptyString(orderId)) {
                            await this.allowedToCompare(orderId, ediOrderRow, changeReplyReq).catch(async (error) => {
                                CommonDialogs.showError(EdiUtility.getCleanErrorMessage(error));
                                log.error("allowedToCompare failed: " + error);
                            });
                        }
                        else {
                            const dialog = new LinkTenderDialog({ okVisible: false, title: "Missing Order ID On Change Tender" });
                            dialog.ediOrderRow = ediOrderRow;
                            await this.setRecordInUse(ediOrderRow, "Y");

                            dialog.show().then(async response => {
                                if (response) {
                                    this.allowedToCompare(ediOrderRow.get("order_id"), ediOrderRow, changeReplyReq).catch(async (error) => {
                                        await this.setRecordInUse(ediOrderRow, "N");
                                        CommonDialogs.showError(EdiUtility.getCleanErrorMessage(error));
                                        log.error("allowedToCompare failed: " + error);
                                    });
                                }
                                else {
                                    await this.setRecordInUse(ediOrderRow, "N");
                                }
                            });
                            dialog.addUnmountListener(() => {
                                this.buttonAccept.busy = false;
                                this.refreshScreen();
                            });
                        }
                    }
                    else {
                        Snackbar.showSnackbar("Change tenders are not eligible for multi-select at this time.  Skipping tender [id: " + ediOrderId + "].");
                    }
                }
                else if (tender.data.get("is_cancel")) {
                    if (processingSingleRow) {
                        if (!StringUtil.isEmptyString(orderId)) {
                            await this.voidOrder(orderId, ediOrderId, cancelReplyReq).then(async () => {
                                log.info("Finished voidOrder - in 'then'.");
                                this.waitingOnInput = false;
                                this.readyForNext = true;
                            });
                        }
                        else {
                            const prompt = "The following tender does not match an existing Order. Manual action may be necessary " +
                                "to void any related Order.\n\n" +
                                "Bill of Lading: " + tender.data.get("bol") + "\n" +
                                "Shipment ID: " + tender.data.get("shipment_id") + "\n" +
                                "Consignee Reference: " + tender.data.get("cons_refno") + "\n" +
                                "Tender ID: " + tender.data.get("tender_id") + "\n\n" +
                                "Do you wish for this tender to remain in Tender Express?";
                            const dlg = CommonDialogs.showYesNo(prompt, "Reply to Tender").then(async result => {
                                const ediOrderRow = await new ModelEdiOrder().searchSingle({ id: ediOrderId }) as RowEdiOrder;

                                if (result === false) {
                                    if (ediOrderRow.get("no_display") != "Y") {
                                        log.info("Excluding edi_order with id [" + ediOrderId + "]");
                                        ediOrderRow.set("no_display", "Y");
                                        ediOrderRow.set("record_in_use", "N");
                                        await ediOrderRow.post();
                                    }
                                }
                                else if (result === true) {
                                    log.info("Unlocking edi_order with id [" + ediOrderId + "]");
                                    await this.setRecordInUse(ediOrderRow, "N");
                                }
                                if (this.selectedRow.data.get("requires_reply_cancel") === "Y") {
                                    this.acceptLoadTender(ediOrderId, orderId).then(async response => {
                                        const batch = response.data[0].reply_batch;

                                        if (batch != "-1" && batch != "0") {
                                            Toast.showSuccessToast("Reply sent with batch " + batch + ".");
                                        }
                                        else {
                                            const error = response.data[0].description;
                                            Snackbar.showWarningSnackbar("Unable to send reply.  " + error);
                                        }

                                        const refreshedEdiOrderRow = await new ModelEdiOrder().searchSingle({ id: ediOrderId }) as RowEdiOrder;
                                        await this.setRecordInUse(refreshedEdiOrderRow, "N");
                                        // .then(async () => {
                                        //     log.info("Starting delay (3s)...")
                                        //     await this.delay(3000).then(async () => {
                                        //         log.info("Delay done.  Refreshing.")
                                        //         await this.refreshScreen();
                                        //     });
                                        // });
                                    });
                                }
                            });
                        }
                    }
                    else {
                        Snackbar.showSnackbar("Cancel tenders are not eligible for multi-select at this time.  Skipping tender [id: " + ediOrderId + "].");
                    }
                }
            }

            let allResults: ModelRow<any>[] = null;

            await Api.post("lme/datafusion/process-originals", {
                "edi_order_list": origTenderList
            }).then(async origResponse => {
                allResults = origResponse.data;

                if (allResults.length > 0) {
                    this.setResults(allResults, null, false);
                }
            });
        } //end express mode
        else {
            log.info("Processing " + selectedRows.length + " tenders using non-express mode.");
            let tendersProcessed = 0;
            const orderResults = [];

            for (let x = 0; x < selectedRows.length; x++) {
                this.readyForNext = false;
                this.waitingOnInput = false;
                const tender = selectedRows[x];
                const ediOrderId: string = tender.data.get("tender_id");
                let orderId: string = tender.data.get("order_id");
                const originalReplyReq = tender.data.get("requires_reply_original");
                const changeReplyReq = tender.data.get("requires_reply_change");
                const cancelReplyReq = tender.data.get("requires_reply_cancel");
                const replyCreated = tender.data.get("reply_created") == true ? "Y" : "N";
                const direction = tender.data.get("direction");
                const ediOrderRow = await this.findEdiOrderRow(ediOrderId);
                const ediOrderProfile = await this.findEdiOrderProfile(ediOrderRow);

                if ("Y" == ediOrderRow.get("record_in_use")) {
                    Snackbar.showSnackbar("Shipment ID: " + ediOrderRow.get("shipment_id") + ". This tender is currently locked, and can not be accepted.", { persist: true });
                    this.buttonAccept.busy = false;
                    return;
                }
                if ("N" == ediOrderRow.get("ignore_map_errors") && 0 < ediOrderRow.get("error_count")[0]) {
                    Snackbar.showWarningSnackbar("Load tender " + ediOrderId + " has errors that must be reviewed prior to order creation.");
                    this.buttonAccept.busy = false;
                    return;
                }
                if (await this.hasInvalidChargeCodes(ediOrderId, direction)) {
                    Snackbar.showWarningSnackbar("Load tender " + ediOrderId + " has invalid/missing other charge codes which must be corrected before creating an order. " +
                        "Please use the View Load Tender screen to correct/process this tender.");
                    this.buttonAccept.busy = false;
                    return;
                }
                if (tender.data.get("is_original")) {
                    let existingOrderId: string = "";
                    let existingOrderStatus: string = "";
                    const duplicateCutoffDays = ediOrderProfile.get("dup_cutoff_days");
                    const result = await Api.search("lme/datafusion/orders-lookup-for-tenders", {
                        search_shipment_id: ediOrderRow.get("shipment_id"),
                        dup_cutoff_days: duplicateCutoffDays
                    });
                    if (result?.data?.length > 0) {
                        existingOrderId = result.data[0].id;
                        existingOrderStatus = result.data[0].status;
                    }
                    else {
                        existingOrderId = "";
                        existingOrderStatus = "";
                    }

                    if (!StringUtil.isEmptyString(existingOrderId) && existingOrderStatus != "V") {
                        CommonDialogs.showYesNo("Order " + existingOrderId + " already exists for this shipment id.  Would you like to swap the purpose of this tender to a Change?",
                            "Order Exists").then(async clickedYes => {
                            if (clickedYes) {
                                await this.swapPurposeExistingTender(ediOrderRow, existingOrderId);

                                CommonDialogs.showYesNo("Would you like to compare this tender to the existing order now?", "Compare Tender").then(async response => {
                                    if (response) {
                                        await this.setRecordInUse(ediOrderRow, "Y");
                                        await this.allowedToCompare(existingOrderId, ediOrderRow, originalReplyReq).then(() => {
                                            log.info("Finished compare.");
                                        }).catch(async (error) => {
                                            await this.setRecordInUse(ediOrderRow, "N");
                                            CommonDialogs.showError(EdiUtility.getCleanErrorMessage(error));
                                            log.error("allowedToCompare failed: " + error);
                                        });
                                    }
                                });

                                this.buttonAccept.busy = false;
                            } else {
                                ediOrderRow.set("order_id", existingOrderId);
                                orderId = existingOrderId;

                                CommonDialogs.showYesNo("Would you like to exclude this tender?", "Exclude Tender", {
                                    titleProps: {
                                        imageName: ImageName.circleX,
                                        imageColor: "primary.light"
                                    }
                                }).then(async response => {
                                    if (response) {
                                        await this.noDisplayTender(ediOrderRow);
                                    } else {
                                        if (originalReplyReq == "Y" && replyCreated != "Y") {
                                            CommonDialogs.showYesNo("Send reply?").then(response => {
                                                if (response) {
                                                    this.acceptLoadTender(ediOrderRow.get("id"), orderId).then(async (result) => {
                                                        this.processReplyStatus(orderId, result, "not compared");
                                                    });
                                                }
                                            });
                                        }
                                    }
                                }).finally(async () => {
                                    this.setRecordInUse(ediOrderRow, "N");
                                    this.buttonAccept.busy = false;
                                    tender.selected = false;
                                });
                            }
                        });
                        tendersProcessed++;
                    } else {
                        let okToMakeOrder = true;

                        if(!StringUtil.isEmptyString(existingOrderId) && existingOrderStatus == "V") {
                            await CommonDialogs.showYesNo("A voided order [" + existingOrderId + "] exists for this shipment id.  Would you like to create a new order?", "Create New Order").then(async clickedYes => {
                                if (clickedYes) {
                                    okToMakeOrder = true;
                                }
                                else {
                                    this.buttonAccept.busy = false;
                                    tender.selected = false;
                                    okToMakeOrder = false;
                                }
                            });
                        }

                        if (okToMakeOrder) {
                            await Api.search("lme/datafusion/make-order", {
                                "edi_order_id": ediOrderId, "calc_max_target": true
                            }).then(async response => {
                                const orderLayout = Layout.getLayout("lme/dispatch/OrderTabView") as OrderTabView;
                                orderLayout["additionalProps"] = { invokedFromEdi: true };
                                const orderPanel = new CrudDecorator({
                                    layout: orderLayout,
                                    providedApiData: response.data[0].order,
                                    mode: DataSourceMode.ADD,
                                    headerProps: {
                                        showClose: true,
                                        showSaveAndClose: true,
                                        showSave: false,
                                        showExecute: true,
                                        doAfterClose: () => {
                                            this.buttonAccept.busy = false;
                                        }
                                    },
                                    doAfterSlideOut: async () => {
                                        const orderTabLayout = orderPanel.layout as OrderTabView;
                                        const orderId = orderTabLayout.layoutOrders.mainDataSource.activeRow.get("id");
                                        const origDestRateId = ediOrderRow.get("orig_dest_rate_id");
                                        const commitmentId = ediOrderRow.get("commitment_id");
                                        if (!StringUtil.isEmptyString(orderId)) {
                                            MakeOrderUtility.addHandlingRequirements(orderId, orderLayout.layoutOrders);
                                            Api.post("lme/datafusion/after-create-order-tasks", {
                                                "edi_order_id": ediOrderId,
                                                "order_id": orderId,
                                                "orig_dest_rate_id": origDestRateId,
                                                "commitment_id": commitmentId
                                            }).then(async () => {
                                                tendersProcessed++;

                                                const rate: string = orderTabLayout.layoutOrders.mainDataSource.activeRow.get("rate");

                                                if (!StringUtil.isEmptyString(rate)) {
                                                    if (parseFloat(rate) != ediOrderRow.get("rate")) {
                                                        orderTabLayout.layoutOrders.mainDataSource.activeRow.post(); //repost so rates recalculate
                                                    }
                                                }

                                                if (this.switchRefresh.checked && x == selectedRows.length - 1) {
                                                    // this.postMakeOrderRefresh(ediOrderRow, originalReplyReq);
                                                    this.buttonAccept.busy = false;
                                                }
                                            }).catch(error => {
                                                log.debug(error);
                                                CommonDialogs.showServerError(error);
                                            });
                                        }
                                        //reply
                                        this.replyToTender(ediOrderId, orderId, originalReplyReq, orderResults, selectedRows, replyCreated, ediOrderRow)
                                        this.setRecordInUse(ediOrderRow, "N").then(() => { this.readyForNext = true; });
                                        this.buttonAccept.busy = false;
                                    },
                                    layoutLoadListeners: (event: Event) => {
                                        const l = event.target as OrderTabView;

                                        l.layoutOrders.currencyFromProfile = ediOrderProfile.get("currency_type_id");
                                        l.layoutOrders.layoutRates.invokedFromEdi = true;
                                        l.layoutOrders.layoutRates.currencyFromProfile = ediOrderProfile.get("currency_type_id");
                                        l.layoutOrders.switchRecurringOrder.visible = false;
                                        l.layoutOrders.buttonEnterNewOrder.visible = false;
                                        l.layoutOrders.panelHowManyOrders.visible = false;
                                        l.layoutOrders.tabNextActions.visible = selectedRows.length == 1;
                                        l.layoutOrders.loadFromApiData(response.data[0].order, DataSourceMode.ADD);
                                        l.layoutOrders.mainDataSource.setProp(true, "isEdi", null);
                                        l.layoutOrders.mainDataSource.setProp(ediOrderProfile.get("id"), "ediProfileId", null);
                                        l.layoutOrders.imageAddHandlingReqOnClick = () => {
                                            if (orderLayout.layoutOrders.sourceBrltlOrderHdrXFgp.data.length == 0 && orderLayout.layoutOrders.layoutHandlingReqs.sourceBrltlOrderHdrXFgp.data.length > 0) {
                                                orderLayout.layoutOrders.sourceBrltlOrderHdrXFgp.data = orderLayout.layoutOrders.layoutHandlingReqs.sourceBrltlOrderHdrXFgp.data;
                                                orderLayout.layoutOrders.layoutHandlingReqs.sourceBrltlOrderHdrXFgp.clear();
                                                orderLayout.layoutOrders.layoutHandlingReqs.sourceBrltlOrderHdrXFgp.setProp("Y", "madeFromEdi", null);
                                            }
                                            OrderHandlingRequirementsSlideout.showSlideout({
                                                hdrDataSource: orderLayout.layoutOrders.sourceBrltlOrderHdrXFgp,
                                                hdrChanged: (row: ModelRow, type: ModelDataChangeType) => orderLayout.layoutOrders.hdrChanged(row, type),
                                                onSave: (hasChanged: boolean) => {
                                                    orderLayout.layoutOrders.layoutHandlingReqs.populateHdrs(orderLayout.layoutOrders.sourceBrltlOrderHdrXFgp.data, true, hasChanged).then(() => {
                                                        orderLayout.layoutOrders.sourceBrltlOrderHdrXFgp.notifyHasChangedComponents(true);
                                                    });
                                                },
                                                invokedFromEdi: true
                                            })
                                        };
                                    }
                                });
                                orderPanel.slideIn({ speed: 200 });
                                await this.setRecordInUse(ediOrderRow, "Y");

                            }).catch(reason => {
                                log.info("Exception making order.  " + reason);
                                Snackbar.showWarningSnackbar("Error encountered creating order. Load tender " + ediOrderId
                                    + " has errors that must be reviewed prior to order creation. Please use the View Load Tender screen to correct/process the tender.");
                                this.buttonAccept.busy = false;
                            });
                        }
                    }
                }
                else if (tender.data.get("is_change")) {
                    tendersProcessed++;
                    if (!processingSingleRow) {
                        CommonDialogs.showError("Change tenders not eligible for multi-select at this time.");
                        this.readyForNext = true;
                        this.buttonAccept.busy = false;
                        return;
                    } else {
                        if (StringUtil.isEmptyString(orderId)) {
                            const result = await this.findOriginalOrderId(ediOrderRow);
                            if (result && result.data && result.data.length > 0) {
                                orderId = result.data[0].order_id;
                            }
                        }

                        if (!StringUtil.isEmptyString(orderId)) {
                            await this.setRecordInUse(ediOrderRow, "Y");
                            await this.allowedToCompare(orderId, ediOrderRow, changeReplyReq).then(() => {
                                log.info("Finished compare.");
                            }).catch(async (error) => {
                                await this.setRecordInUse(ediOrderRow, "N");
                                CommonDialogs.showError(EdiUtility.getCleanErrorMessage(error));
                                log.error("allowedToCompare failed: " + error);
                            });
                        }
                        else if (StringUtil.isEmptyString(orderId)) {
                            const dialog = new LinkTenderDialog({ okVisible: false, title: "Missing Order ID On Change Tender" });
                            dialog.ediOrderRow = ediOrderRow;
                            await this.setRecordInUse(ediOrderRow, "Y");

                            dialog.show().then(async response => {
                                if (response) {
                                    this.allowedToCompare(ediOrderRow.get("order_id"), ediOrderRow, changeReplyReq).catch(async (error) => {
                                        await this.setRecordInUse(ediOrderRow, "N");
                                        CommonDialogs.showError(EdiUtility.getCleanErrorMessage(error));
                                        log.error("allowedToCompare failed: " + error);
                                    });
                                }
                                else {
                                    await this.setRecordInUse(ediOrderRow, "N");
                                }
                            });
                            dialog.addUnmountListener(async () => {
                                this.buttonAccept.busy = false;
                            });
                        }
                    }
                }
                else if (tender.data.get("is_cancel")) {
                    tendersProcessed++;
                    if (!processingSingleRow) {
                        CommonDialogs.showError("Cancel tenders not eligible for multi-select at this time.");
                        this.readyForNext = true;
                        this.buttonAccept.busy = false;
                        return;
                    }

                    if (!StringUtil.isEmptyString(orderId)) {
                        await this.voidOrder(orderId, ediOrderId, cancelReplyReq).then(async () => {
                            this.waitingOnInput = false;
                            this.readyForNext = true;
                        });
                    }
                    else {
                        log.info("Order ID not on tender.  Looking for related tenders...");

                        await this.findOrderId(ediOrderRow).then(result => {
                            const lookupOrderId = (result.data.length > 0) ? result.data[0].order_id : "";

                            if (!StringUtil.isEmptyString(lookupOrderId)) {
                                this.voidOrder(lookupOrderId, ediOrderId, cancelReplyReq);
                            }
                            else {
                                const reqsReply = this.selectedRow.data.get("requires_reply_cancel") === "Y";
                                //TODO -- need to discuss behavior, this chunk will never be executed.  leaning towards alterring where clause to display these cancels.
                                if (!reqsReply) {
                                    const prompt = "The following tender does not match an existing Order.  Manual action may be necessary " +
                                        "to void any related Order.\n\n" +
                                        "Bill of Lading: " + tender.data.get("bol") + "\n" +
                                        "Shipment ID: " + tender.data.get("shipment_id") + "\n" +
                                        "Consignee Reference: " + tender.data.get("cons_refno") + "\n" +
                                        "Tender ID: " + tender.data.get("tender_id") + "\n\n" +
                                        "Do you wish for this tender to remain in Tender Express?";
                                    const dlg = CommonDialogs.showYesNo(prompt, "Display Cancel").then(async result => {
                                        if (result != undefined && !result) {
                                            if (ediOrderRow.get("no_display") != "Y") {
                                                log.info("Excluding edi_order with id [" + ediOrderId + "]");
                                                ediOrderRow.set("no_display", "Y");
                                                ediOrderRow.set("record_in_use", "N");
                                                ediOrderRow.post();
                                            }
                                        }
                                        else if (result != undefined) {
                                            log.info("Unlocking edi_order with id [" + ediOrderId + "]");
                                            await this.setRecordInUse(ediOrderRow, "N");
                                            this.buttonAccept.busy = false;
                                        }
                                    }).finally(() => {
                                        this.buttonAccept.busy = false;
                                    });
                                }
                                else {
                                    const prompt = "The following tender does not match an existing Order.  Manual action may be necessary " +
                                        "to void any related Order.\n\n" +
                                        "Bill of Lading: " + tender.data.get("bol") + "\n" +
                                        "Shipment ID: " + tender.data.get("shipment_id") + "\n" +
                                        "Consignee Reference: " + tender.data.get("cons_refno") + "\n" +
                                        "Tender ID: " + tender.data.get("tender_id") + "\n\n"
                                    CommonDialogs.showDialog(prompt).then(response => {
                                        this.acceptLoadTender(ediOrderId, orderId).then(async response => {
                                            const batch = response.data[0].reply_batch;
                                            Toast.showSuccessToast("Reply sent with batch " + batch + ".");
                                            await this.delay(2000).then(() => {
                                                log.info("Delay done.  Refreshing.")
                                                this.refreshScreen();
                                            });
                                        });
                                    }).finally(() => {
                                        this.buttonAccept.busy = false;
                                    });
                                }
                            }
                        });
                    }
                }  //end purpose if/else

                while (!this.readyForNext || this.waitingOnInput) {
                    log.debug("Waiting for user input or processing to complete...");
                    await this.delay(1000);
                }
            }  //end for loop - done processing tenders

            if (!processingSingleRow) {
                let counter = 0;
                while (orderResults.length < selectedRows.length && counter < 60 * 5) {
                    log.info("Waiting for orderResults to be populated. Orders processed: " + orderResults.length + " of " + selectedRows.length);
                    await this.delay(1000);
                    counter++;
                }
            }

            if (selectedRows.length > 1 && tendersProcessed == selectedRows.length) {
                this.setResults(null, orderResults, true);
            }

        }
        this.buttonAccept.busy = false;
    }

    createReplyModelRow(comp: string): ModelRow<any> {
        const compResult: string[] = comp.split(",");

        return new ModelRow<any>("lme/datafusion/reply-results", true, {
            orderid_out: compResult[0],
            tenderid_out: compResult[1],
            purpose_out: compResult[2],
            success_out: compResult[3]
        });
    }

    async setResults(results: ModelRow[], resultAsString: string[], forceResultPanel: boolean) {
        if (((results != null && results.length > 1) || (resultAsString != null && resultAsString.length > 1)) || forceResultPanel) {
            const replyResultPanel = Layout.getLayout("lme/datafusion/ReplyResults", {
                width: window.innerWidth * 0.60,
                height: window.innerHeight * 0.80,
                scrollY: true,
                backgroundColor: "defaultBackground",
                borderWidth: 1,
                borderShadow: true,
                borderColor: "strokeSecondary",
                fillHeight: true,
                paddingBottom: 50
            }) as ReplyResults;

            replyResultPanel.addLayoutLoadListener(() => {
                replyResultPanel.insert(headingPanel, 0);
                replyResultPanel.sourceReplyResults.search({ result_list: resultString });
                replyResultPanel.slideIn({ direction: Alignment.RIGHT }, true);
            });

            const headingPanel = new Panel({ id: "result-panel-header", fillRow: true, backgroundColor: "primary.darker", paddingLeft: 10, color: "primary.reverse" });
            const replyTitle = new Label({
                text: "Processing Results",
                imageMarginRight: 8,
                rowBreak: false,
                align: HorizontalAlignment.LEFT,
                fontBold: true,
                fontSize: "large"
            });

            const resultCloseButton = new Button({ imageName: ImageName.x, variant: ButtonVariant.round, imageHeight: 28, imageWidth: 28, cancel: true, rowBreak: false });
            resultCloseButton.addClickListener(_event => {
                replyResultPanel.slideOut();
            });

            const goToOrderButton = new Button({ imageName: ImageName.detail, rowBreak: false });
            goToOrderButton.addClickListener(() => {
                goToOrderButton.tooltip = "Click to view the selected order";
                goToOrderButton.tooltipPosition = Alignment.LEFT;

                if (replyResultPanel.replyResultsTable.selectedRow !== null) {
                    const orderId = replyResultPanel.replyResultsTable.selectedRow.data.get("orderid_out");
                    if (!StringUtil.isEmptyString(orderId) && orderId != "N/A" && orderId != "undefined") {
                        OrderTabView.navigateTo({ key: orderId, newTab: true });
                    } else {
                        Snackbar.showSnackbar("Selected tender is not bound to an order.", { persist: true });
                    }
                }
            });

            headingPanel.add(replyTitle, new HorizontalSpacer(), goToOrderButton, resultCloseButton);

            let resultString: string = "";

            if (StringUtil.isEmptyString(resultAsString)) {
                let needSeparator: boolean = false;

                results.forEach(line => {
                    if (needSeparator)
                        resultString += "|";
                    else
                        needSeparator = true;

                    const purposeDesc = this.getPurposeDesc(line["purpose"]);
                    const successDesc = this.getSuccessDesc(line["success"]);
                    resultString += line["order_id"] + "," + line["tender_id"] + "," + purposeDesc + "," + line["description"];
                });
            }
            else {
                let needSeparator: boolean = false;

                resultAsString.forEach(line => {
                    if (needSeparator)
                        resultString += "|";
                    else
                        needSeparator = true;

                    const orderArray: string[] = line.split(",");
                    resultString += orderArray[0] + "," + orderArray[1] + "," + orderArray[2] + "," + orderArray[3] + "," + orderArray[4];
                });
            }

            await Api.search("lme/datafusion/reply-results", {
                result_list: resultString
            }).then(async () => {
                //TODO - do we need to do something here???
                // replyResultPanel.slideOut();
            });
        }
        else {
            results.forEach(line => {
                const resultOrderId = line["order_id"];
                const description = StringUtil.stringAfter(line["description"], "Order created. ");

                Toast.showToast(this.getResultsToastPanel(resultOrderId, description, "created"), null, { millisUntilDismissal: 3000 });
            });

        }
    }

    async findOrderId(ediOrderRow): Promise<any> {
        const partnerId = ediOrderRow.get("partner_id");
        const shipmentId = ediOrderRow.get("shipment_id");
        const version = ediOrderRow.get("version");
        const tenderId = ediOrderRow.get("id");

        return await Api.search("lme/datafusion/find-order-id", { partner_id: partnerId, shipment_id: shipmentId, version: version, tender_id: tenderId });
    }

    async findEdiOrderRow(ediOrderId: string): Promise<RowEdiOrder> {
        // Check cache first
        if (this._ediOrderCache.has(ediOrderId)) {
            return this._ediOrderCache.get(ediOrderId);
        }

        // Try to get from main data source first (already loaded data)
        const existingRow = this.mainDataSource.data?.find(row => row.get("tender_id") === ediOrderId);
        if (existingRow) {
            // For performance, use a simplified approach - query server but cache result
            const ediOrderRow = await new ModelEdiOrder().searchSingle({ id: ediOrderId }) as RowEdiOrder;
            if (ediOrderRow) {
                this._ediOrderCache.set(ediOrderId, ediOrderRow);
            }
            return ediOrderRow;
        }

        // Fallback to server query if not in cache or main data
        const ediOrderRow = await new ModelEdiOrder().searchSingle({ id: ediOrderId }) as RowEdiOrder;
        if (ediOrderRow) {
            this._ediOrderCache.set(ediOrderId, ediOrderRow);
        }
        return ediOrderRow;
    }

    async findEdiOrderProfile(ediOrderRow: RowEdiOrder): Promise<RowEdiorderProfile> {
        const cacheKey = `${ediOrderRow.data["partner_id"]}_${ediOrderRow.data["version"]}`;

        // Check cache first
        if (this._ediOrderProfileCache.has(cacheKey)) {
            return this._ediOrderProfileCache.get(cacheKey);
        }

        // Fetch from server and cache result
        const profile = await new ModelEdiorderProfile().searchSingle({
            partner_id: ediOrderRow.data["partner_id"],
            version: ediOrderRow.data["version"]
        }) as RowEdiorderProfile;

        if (profile) {
            this._ediOrderProfileCache.set(cacheKey, profile);
        }
        return profile;
    }

    async updateOrderId(ediOrderRow: RowEdiOrder, orderId: string) {
        if (!StringUtil.isEmptyString(orderId)) {
            ediOrderRow.set("order_id", orderId);
            await ediOrderRow.post();
        }
    }

    async acceptLoadTender(ediOrderId: string, orderId?: string): Promise<any> {
        return new Promise(async (resolve) => {
            resolve(await this.sendReply(ediOrderId, orderId));
        });
    }

    async sendReply(ediOrderId: string, orderId?: string): Promise<any> {
        log.info("Replying to tender " + ediOrderId + ".")
        return await Api.post("lme/datafusion/accept-tender", {
            edi_order_id: ediOrderId,
            order_id: orderId
        });
    }

    async voidOrder(orderId: string, ediOrderId: string, needsReply: string, callback?): Promise<any> {
        this.waitingOnInput = true;

        return new Promise(async (resolve) => {
            const orderRowStatus = await this.getOrderRowStatus(orderId);
            const ediOrderRow = await new ModelEdiOrder().searchSingle({ id: ediOrderId }) as RowEdiOrder;
            const replyBatch = ediOrderRow.get("reply_batch_id");

            if (orderRowStatus == "V") {
                if (needsReply == "Y" && replyBatch == "0") {
                    const prompt = "Order is already voided.  Would you still like to send the reply?";
                    const dlg = await CommonDialogs.showYesNo(prompt, "Void Tender").then(result => {
                        if (result == true) {
                            this.acceptLoadTender(ediOrderId, orderId).then(result => {
                                this.processReplyStatus(orderId, result, "already voided");
                                this.refreshScreen();
                            });
                            resolve(true);
                        }
                        else if (result == false) {
                            CommonDialogs.showMessage("Order is already voided.  Removing from Tender Express.").then(() => {
                                this.excludeExistingTender(ediOrderRow);
                                resolve(true);
                            });
                        }
                    }).finally(() => {
                        this.buttonAccept.busy = false;
                    });
                }
            }
            else {
                Api.post("lme/datafusion/df-void-order", { order_id: orderId, tender_id: ediOrderId }).then(async result => {
                    const status = result.data[0].new_status;
                    const text = result.data[0].text;

                    if (status == "V") {
                        if (needsReply == "Y") {
                            await this.acceptLoadTender(ediOrderId, orderId).then(response => {
                                const batch = response.data[0].reply_batch;

                                if (batch != "-1" && batch != "0") {
                                    Toast.showToast(this.getResultsToastPanel(orderId, "Reply sent with batch " + batch + ".", 'successfully voided'), null, { millisUntilDismissal: 7000 });
                                }
                                else {
                                    Snackbar.showWarningSnackbar("Order " + orderId + " successfully voided.  Unable to send reply.");
                                }

                                this.waitingOnInput = false;
                                resolve(true);
                                this.buttonAccept.busy = false;
                            });
                        }
                        else {
                            Toast.showToast(this.getResultsToastPanel(orderId, "Partner does not require a reply.", 'successfully voided'), null, { millisUntilDismissal: 7000 });
                            this.waitingOnInput = false;
                            resolve(true);
                        }
                    }
                    else {
                        if (needsReply == "Y") {
                            CommonDialogs.showYesNo("Order " + orderId + " not voided.  " + text + "\nWould you still like to accept the tender?", "Reply to Tender",
                                {
                                    titleProps: {
                                        imageName: ImageName.circleX,
                                        imageColor: "primary.light"
                                    }
                                }).then(async yes => {
                                    if (yes) {
                                        await this.acceptLoadTender(ediOrderId, orderId).then(response => {
                                            const batch = response.data[0].reply_batch;

                                            if (batch != "-1" && batch != "0") {
                                                Toast.showSuccessToast("Reply sent with batch " + batch + ".");
                                            }
                                            else {
                                                const error = response.data[0].description;
                                                Snackbar.showWarningSnackbar("Unable to send reply.  " + error);
                                            }
                                        });
                                    }

                                    this.waitingOnInput = false;
                                    resolve(true);
                                }).finally(() => {
                                    this.buttonAccept.busy = false;
                                });
                        }
                    }
                });
            }
        });
    }

    async excludeTender() {
        if (ArrayUtil.isEmptyArray(this.selectedRows)) {
            Snackbar.showWarningSnackbar("No rows selected.  Please select a row before excluding.")
            this.disableReplyOptions();
            return;
        }
        const lengthOfSelectedRows = this.selectedRows?.length;
        if (lengthOfSelectedRows > 0) {
            const beginningDialog = "This will mark ";
            const endingDialog = lengthOfSelectedRows === 1
                ? " record for exclusion. Do you wish to continue?"
                : " records for exclusion. Do you wish to continue?";
            await CommonDialogs.showYesNo(beginningDialog + lengthOfSelectedRows + endingDialog).then(async (clickedYes) => {
                if (clickedYes) {
                    for (const currRow of this.selectedRows) {
                        const ediOrderId = currRow.data.get("tender_id");
                        const ediOrderRow = await new ModelEdiOrder().searchSingle({ id: ediOrderId }) as RowEdiOrder;
                        await this.noDisplayTender(ediOrderRow);
                    }
                    this.disableReplyOptions();
                }
            });
        }
    }

    async declineTender() {
        if (ArrayUtil.isEmptyArray(this.selectedRows)) {
            Snackbar.showWarningSnackbar("No rows selected.  Please select a row before declining.")
            this.disableReplyOptions();
            return;
        }

        if (this.selectedRows.length > 1) {
            Snackbar.showSnackbar("Multi-select is not permitted for this feature yet.  Please select an individual row.", { persist: true });
            this.refreshScreen();
            return;
        }

        const ediOrderId: string = this.selectedRow.data.get("tender_id");
        const ediOrderRow = await this.findEdiOrderRow(ediOrderId);
        const dt = new DeclineTender();
        dt.ediOrderRow = ediOrderRow;
        dt.declineRequiresCode = this.selectedRow.data.get("decline_requires_code");
        dt.decline();
    }

    async defaultProfileId() {
        const userId = LMEUserSettings.getUserId();

        if (!StringUtil.isEmptyString(userId)) {
            // Use cached user settings if available
            if (this._userSettingsCache) {
                this.lookupLtxProfileRow(this._userSettingsCache.get("ltexpress_prof_id"));
            } else {
                await new ModelEdiUser().searchSingle({ id: userId }).then(row => {
                    if (row != null) {
                        this._userSettingsCache = row; // Cache for session
                        this.lookupLtxProfileRow(row.get("ltexpress_prof_id"));
                    }
                });
            }
        }

        if (this.ltxProfileId == null) {
            const panel = new Panel({backgroundColor: "warning"});

            const label = new Label({ text: "You do not have a default Tender Express profile defined. You can select a default " +
                "profile by checking 'My Profile' on the Tender Express Profile screen or you can set the Tender Express Profile " +
                "in User Settings." });

            let snackbar: Snackbar = new Snackbar();

            const button1 = new Button({
                caption: "Open User Settings",
                rowBreak: false,
                fillRow: true,
                onClick: () => {
                    UserLayoutProvider.navigateTo();
                    if (snackbar) {
                        snackbar.dismiss();
                    }
                }
            });
            const button2 = new Button({
                caption: "Open Tender Express Profiles",
                fillRow: true,
                onClick: (event) => {
                    Navigation.navigateTo("/lme/datafusion/LoadTenderExpressProfileList");
                    if (snackbar) {
                        snackbar.dismiss();
                    }
                }
            });

            panel.add(label);
            panel.add(button1);
            panel.add(button2);
            snackbar = Snackbar.showWarningSnackbar(panel, { persist: true });
        }
    }

    private async findSelectedProfile() {
        const prof: RowEdiLtxexpressProf = await this.getLoadTenderExpressProfile(this._ltxProfileId);
        return prof;
    }

    public async lookupLtxProfileRow(id: string) {
        if (!StringUtil.isEmptyString(id)) {
            this._ltxProfileId = id;
            this.setLtxTitle(this._ltxProfileId);
            this.setLtxProfileRow(await this.findSelectedProfile());
            this.refreshScreen();
        }
    }
    public setLtxProfileRow(row: RowEdiLtxexpressProf) {
        this._ltxProfile = row;
        this.filterObject = new LoadTenderExpressFilter(this._ltxProfile);
    }
    async setLtxTitle(value: string = "") {
        const ltxProfile = value ? await this.getLoadTenderExpressProfile(value) : this._ltxProfile;
        if (ltxProfile != null) {
            this.ltxProfileTextbox.text = ltxProfile.get("title", ltxProfile.get("id"));
            this._expressMode = ltxProfile.get("use_express_mode", "N");
            this._showResultPanel = ltxProfile.get("show_result_panel", "Y");
            this._showNewestFirst = ltxProfile.get("sort_descend", "N");  //recently updated LME to default this to 'N'
        }
    }

    refreshScreen() {
        if (!StringUtil.isEmptyString(this.ltxProfileId)) {
            this.disableReplyOptions();
            this.updateRefreshLabel();
        }
    }

    async postMakeOrderRefresh(ediOrderRow: ModelRow<any>, needsReply: string) {
        let readyForRefresh: boolean = false;
        let counter: number = 0;

        while (!readyForRefresh && counter < 10) {
            counter++;
            await this.delay(1000).then(async () => {
                log.info("Delay done.  Try #" + counter);
                const orderId = ediOrderRow.get("order_id", "");
                const ediOrderId = ediOrderRow.get("id", "");

                if (!StringUtil.isEmptyString(orderId)) {
                    if (needsReply == "Y") {
                        await Api.search("lme/datafusion/verify-reply", { tender_id: ediOrderId }).then(async result => {
                            const replyCreated = result.data[0].reply_created;
                            if (replyCreated == "Y") {
                                readyForRefresh = true;
                            }
                        });
                    }
                    else {
                        readyForRefresh = true;
                    }
                }
            });
        }
    }

    async updateInterval() {
        const ltxProfileRow = await this.getLoadTenderExpressProfile(this._ltxProfileId);
        clearInterval(this._intervalId);
        log.info("Using profile " + this.ltxProfileId)
        if (ltxProfileRow.get("refresh_rate") > 0) {
            this._interval = ltxProfileRow.get("refresh_rate");
            log.info("Set refresh rate for " + this.ltxProfileId + " to " + this._interval);
        }
    }

    async compareTender(ediOrderRow: RowEdiOrder, needsReply: string, orderIdOverride?: string) {
        const compare = new CompareTender();
        await compare.compareTender(ediOrderRow, needsReply === "Y", undefined, orderIdOverride).then(async result => {
            this.buttonAccept.busy = false;
            log.info("Starting delay (3s)...")
            this.delay(3000).then(async () => {
                if (result) {
                    log.info("Compare tender completed successfully.");
                } else {
                    log.info("Compare not completed.");
                }
            });
        }).catch(async (error) => {
            this.buttonAccept.busy = false;
            log.error("Compare tender failed: " + error);
            throw error; // Re-throw so callers can handle unlocking and error display
        });
    }

    processReplyStatus(orderId: string, response: any, action: string): void {
        const batch = response.data[0].reply_batch;
        const description = response.data[0].description;

        if (batch != "-1" && batch != 0) {
            Toast.showToast(this.getResultsToastPanel(orderId, description, action), null, { millisUntilDismissal: 7000 });
        }
        else if (batch != 0) {
            Snackbar.showWarningSnackbar("Unable to send reply.");
        }
    }

    getResultsToastPanel(orderId: string, message: string, action: string): Panel {
        const labelProps = { color: "white", fontSize: "xlarge", rowBreak: false, paddingLeft: 3, paddingRight: 1, marginLeft: 0, marginRight: 0, wrap: false };
        const labelLinkProps = { ...labelProps, style: { textDecoration: "underline" } };

        if (!StringUtil.isEmptyString(orderId)) {
            return new Panel({
                align: HorizontalAlignment.CENTER, fillRow: true,
                components: [
                    new Label({ ...labelProps, text: "Order " }),
                    new Label({ ...labelLinkProps, text: orderId, onClick: event => OrderTabView.navigateTo({ key: orderId, newTab: true }) }),
                    new Label({ ...labelProps, text: " " + action + ".  " + message })
                ]
            });
        }
        else {
            return new Panel({
                align: HorizontalAlignment.CENTER, fillRow: true,
                components: [
                    new Label({ ...labelProps, text: "Order not " + action + ".  " + message })
                ]
            });
        }
    }

    async allowedToCompare(orderId: string, ediOrderRow: RowEdiOrder, needsReply?: string): Promise<boolean> {
        if (StringUtil.isEmptyString(orderId)) {
            return false;
        }

        const orderRowStatus = await this.getOrderRowStatus(orderId);
        if (orderRowStatus == "D") {
            if (PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_DEL_ORDER)) {
                CommonDialogs.showYesNo("Order is delivered.  Compare not allowed due to permissions settings.  Would you like to remove this tender from Tender Express (exclude)?", "Tender Express",
                    { titleProps: { imageName: ImageName.circleX, imageColor: "primary.light" } }).then(async response => {
                        if (response) {
                            await this.noDisplayTender(ediOrderRow);
                        }
                    }).finally(async () => {
                        this.setRecordInUse(ediOrderRow, "N");
                        this.buttonAccept.busy = false;
                    });
                return false;
            }
            else {
                CommonDialogs.showYesNo("Order " + orderId + " is delivered.  Would you still like to run the compare?", "Compare Delivered Order",
                    { titleProps: { imageName: ImageName.circleX, imageColor: "primary.light" } }).then(async yes => {
                        if (yes) {
                            //set record in use to Y before comparing
                            this.setRecordInUse(ediOrderRow, "Y");
                            if (!StringUtil.isEmptyString(orderId)) {
                                this.compareTender(ediOrderRow, needsReply, orderId).catch(async (error) => {
                                    await this.setRecordInUse(ediOrderRow, "N");
                                    CommonDialogs.showError(EdiUtility.getCleanErrorMessage(error));
                                });
                            } else if (StringUtil.isEmptyString(orderId)) {
                                const dialog = new LinkTenderDialog({ okVisible: false, title: "Missing Order ID On Change Tender" });
                                dialog.ediOrderRow = ediOrderRow;

                                dialog.show().then(response => {
                                    if (response)
                                        this.compareTender(ediOrderRow, needsReply, orderId).then(result => {
                                            if ("Y" == ediOrderRow.get("order_compared")) {
                                                Toast.showSuccessToast("Successful Reply and Order Comparing.", "Order Number " + ediOrderRow.get("order_id"), ImageName.magnifyingGlass, { millisUntilDismissal: 7000 });
                                            }
                                        }).catch(async (error) => {
                                            await this.setRecordInUse(ediOrderRow, "N");
                                            CommonDialogs.showError(EdiUtility.getCleanErrorMessage(error));
                                        });
                                });
                            }

                            return true;
                        }
                        else {
                            if(ediOrderRow.get("no_display") === "N"){
                                CommonDialogs.showYesNo("Would you like to remove this tender from Tender Express (exclude)?", "Tender Express",
                                    {
                                        titleProps: {
                                            imageName: ImageName.circleX,
                                            imageColor: "primary.light"
                                        }
                                    }).then(async response => {
                                        if (response) {
                                            await this.noDisplayTender(ediOrderRow);
                                        } else {
                                            if (needsReply == "Y") {
                                                CommonDialogs.showYesNo("Send reply?").then(response => {
                                                    if (response) {
                                                        this.acceptLoadTender(ediOrderRow.get("id"), orderId).then(async (result) => {
                                                            this.processReplyStatus(orderId, result, "not compared");
                                                        });
                                                    }
                                                });
                                            }
                                        }
                                    }).finally(async () => {
                                        this.setRecordInUse(ediOrderRow, "N");
                                    });
                            }
                            return false;
                        }
                    }).finally(async () => {
                        this.buttonAccept.busy = false;
                    });
            }
        }
        else if (orderRowStatus == "V") {
            CommonDialogs.showYesNo("Order is voided.  Compare not allowed.  Would you like to remove this tender from Tender Express (exclude)?", "Tender Express",
                { titleProps: { imageName: ImageName.circleX, imageColor: "primary.light" } }).then(async response => {
                    if (response) {
                        await this.noDisplayTender(ediOrderRow);
                    } else {
                        if (needsReply == "Y") {
                            CommonDialogs.showYesNo("Send reply?").then(response => {
                                if (response) {
                                    this.acceptLoadTender(ediOrderRow.get("id"), orderId).then(async (result) => {
                                        this.processReplyStatus(orderId, result, "not compared - voided");
                                    });
                                }
                            });
                        }
                    }
                }).finally(async () => {
                    this.setRecordInUse(ediOrderRow, "N");
                    this.buttonAccept.busy = false;
                });
        }
        else {
            if (!StringUtil.isEmptyString(orderId)) {
                try {
                    await this.setRecordInUse(ediOrderRow, "Y");
                    await this.compareTender(ediOrderRow, needsReply, orderId);
                } catch (exception) {
                    await this.setRecordInUse(ediOrderRow, "N");
                    CommonDialogs.showError(EdiUtility.getCleanErrorMessage(exception));
                    throw exception;
                }
            } else if (StringUtil.isEmptyString(orderId)) {
                //set record in use to Y before comparing
                await this.setRecordInUse(ediOrderRow, "Y");

                const dialog = new LinkTenderDialog({ okVisible: false, title: "Missing Order ID On Change Tender" });
                dialog.ediOrderRow = ediOrderRow;

                dialog.show().then(async (response) => {
                    if (response) {
                        try {
                            await this.compareTender(ediOrderRow, needsReply, orderId);
                            log.info("Compare completed.");
                        } catch (reason) {
                            await this.setRecordInUse(ediOrderRow, "N");
                            CommonDialogs.showError(EdiUtility.getCleanErrorMessage(reason));
                        }
                    } else {
                        // Ensure unlock when user cancels the dialog
                        await this.setRecordInUse(ediOrderRow, "N");
                    }
                }).catch(async (error) => {
                    // Ensure unlock on any dialog error
                    await this.setRecordInUse(ediOrderRow, "N");
                    log.error("Dialog error: " + error);
                });
            }
        }
        return true;
    }

    async getOrderRowStatus(orderId: string): Promise<string> {
        // Check cache with TTL
        const cached = this._orderStatusCache.get(orderId);
        const now = Date.now();

        if (cached && (now - cached.timestamp) < this.CACHE_TTL_MS) {
            return cached.status;
        }

        // Fetch from server and cache with timestamp
        const rowOrder = await new ModelOrders().searchSingle({ id: orderId }, "lme/dispatch/Orders");
        const status = rowOrder.get("status", "");

        this._orderStatusCache.set(orderId, { status, timestamp: now });
        return status;
    }

    async delay(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    getPurposeDesc(purpose: string) {
        let purp: string = "";

        if (purpose == "O") {
            purp = "Original";
        }
        else if (purpose == "U") {
            purp = "Change";
        }
        else if (purpose == "C") {
            purp = "Cancel";
        }
        else {
            purp = "Unknown";
        }

        return purp;
    }

    getSuccessDesc(success: string) {
        let suc: string = "";

        if (success == "Y") {
            suc = "Yes";
        }
        else {
            suc = "No";
        }
        return suc;
    }

    enableReplyOptions() {
        if (this.buttonAccept != null) this.buttonAccept.enabled = true;
        if (this.buttonAccept != null) this.buttonAccept.visible = true;
        if (this.buttonDecline != null) this.buttonDecline.enabled = true;
        if (this.buttonDecline != null) this.buttonDecline.visible = true;
        if (this.buttonExclude != null) this.buttonExclude.enabled = true;
    }

    disableReplyOptions() {
        if (this.buttonAccept != null) this.buttonAccept.enabled = false;
        if (this.buttonDecline != null) this.buttonDecline.enabled = false;
        if (this.buttonExclude != null) this.buttonExclude.enabled = false;
    }

    disableAndHideReplyOptions() {
        if (this.buttonAccept != null) this.buttonAccept.enabled = false;
        if (this.buttonAccept != null) this.buttonAccept.visible = false;
        if (this.buttonDecline != null) this.buttonDecline.enabled = false;
        if (this.buttonDecline != null) this.buttonDecline.visible = false;
        if (this.buttonExclude != null) this.buttonExclude.enabled = false;
    }

    enableProcessOption() {
        if (this.buttonAccept != null) this.buttonAccept.enabled = true;
        if (this.buttonAccept != null) this.buttonAccept.visible = true;
        if (this.buttonDecline != null) this.buttonDecline.enabled = false;
        if (this.buttonExclude != null) this.buttonExclude.enabled = true;
    }

    sourceLoadTenderExpressAfterExecution() {
        const ds = this.mainDataSource.data;
        this.layoutAll.mainDataSource.data = ds;
        this.layoutAll.mainDataSource.displayDataInBoundComponents();
        this.setCaption(this.tabAll, this.layoutAll);

        this.layoutOriginal.mainDataSource.data = (ds.filter(tender => tender.data["is_original"]));
        this.setCaption(this.tabOriginal, this.layoutOriginal);
        this.layoutOriginal.mainDataSource.displayDataInBoundComponents();

        this.layoutChange.mainDataSource.data = (ds.filter(tender => tender.data["is_change"]));
        this.setCaption(this.tabChange, this.layoutChange);
        this.layoutChange.mainDataSource.displayDataInBoundComponents();

        this.layoutCancel.mainDataSource.data = (ds.filter(tender => tender.data["is_cancel"]));
        this.setCaption(this.tabCancel, this.layoutCancel);
        this.layoutCancel.mainDataSource.displayDataInBoundComponents();
    }

    public async handleOrderExists(orderId: string, shipmentId: string, ediOrderRow: RowEdiOrder, orderResults: Array<string>) {
        const prompt = "   Order [" + orderId + "] exists for shipment ID " + shipmentId.trim();
        const dialogProps: Partial<YesNoDialogProps> = { noButtonCaption: "Exclude Tender", yesButtonCaption: "Change Purpose" };
        const dlg = await CommonDialogs.showYesNo(prompt, "Order creation not allowed", dialogProps).then(result => {
            const defaultRow = "N/A" + "," + ediOrderRow.data.id + "," + "O" + "," + "Tender skipped.";
            let newRow: string = "";
            if (result === true) {
                this.swapPurposeExistingTender(ediOrderRow, orderId);
                newRow = "N/A" + "," + ediOrderRow.data.id + "," + "O" + "," + "Purpose swapped.";
            } else if (result === false) {
                this.excludeExistingTender(ediOrderRow);
                newRow = "N/A" + "," + ediOrderRow.data.id + "," + "O" + "," + "Tender excluded.";
            }
            //blech blech blech - have to do this because unmount listener fires before button click logic.
            const index = orderResults.indexOf(defaultRow, 0);
            if (index > -1) {
                orderResults.splice(index, 1);
            }
            orderResults.push(newRow);
            this.readyForNext = true;
        });
    }

    async excludeExistingTender(ediOrderRow: RowEdiOrder) {
        ediOrderRow.set("no_display", "Y");
        await ediOrderRow.post();
        return;
    }
    async swapPurposeExistingTender(ediOrderRow: RowEdiOrder, orderId: string) {
        ediOrderRow.set("order_id", orderId);
        ediOrderRow.set("purpose_type", "U");
        ediOrderRow.set("original_order", "N");
        ediOrderRow.set("changed_order", "Y");
        await ediOrderRow.post();
        return;
    }

    public get readyForNext(): boolean {
        return this._readyForNext;
    }

    public set readyForNext(value: boolean) {
        this._readyForNext = value;
    }

    public get waitingOnInput(): boolean {
        return this._waitingOnInput;
    }

    public set waitingOnInput(value: boolean) {
        this._waitingOnInput = value;
    }
    async hasInvalidChargeCodes(tenderId: string, direction: string): Promise<boolean> {
        let invalidChargeCode = false;
        const otherCharges = await new ModelOtherChargeEdi().search({ order_id: tenderId, direction: direction });
        if (otherCharges) {
            for (const modelRow of otherCharges?.modelRows) {
                if (modelRow.get("charge_id") === null || modelRow.get("charge_id") === undefined) {
                    invalidChargeCode = true;
                    break;
                }
            }
        }
        return invalidChargeCode;
    }

    replyToTender(ediOrderId: string, orderId: string, originalReplyReq: string, orderResults: any[], selectedRows: TableRow[], replyCreated: string, ediOrderRow: RowEdiOrder) {
        if (StringUtil.isEmptyString(orderId) !== true) {
            if (originalReplyReq == "Y") {
                this.acceptLoadTender(ediOrderId, orderId).then(response => {
                    if (response != null) {
                        const batch = response.data[0].reply_batch;
                        const description = response.data[0].description;

                        if (batch != "-1" && batch != "0") {
                            if (selectedRows.length == 1) {
                                Toast.showToast(this.getResultsToastPanel(orderId, description, "created"), null, { millisUntilDismissal: 7000 });
                            }
                            const newRow = orderId + "," + ediOrderId + "," + "O" + "," + description;
                            orderResults.push(newRow);
                        }
                        else {
                            const error = response.data[0].description;
                            const message = "Order " + orderId + " created.  Unable to send reply.  " + error;
                            Snackbar.showWarningSnackbar(message);
                            const newRow = orderId + "," + ediOrderId + "," + "O" + "," + message;
                            orderResults.push(newRow);
                        }
                    }
                    else {
                        const message = "Order " + orderId + "created.  Unable to send reply.";
                        Snackbar.showWarningSnackbar(message);
                        const newRow = orderId + "," + ediOrderId + "," + "O" + "," + message;
                        orderResults.push(newRow);
                    }

                }).catch(error => {
                    const message = "Order " + orderId + "created.  Unable to send reply.";
                    Snackbar.showWarningSnackbar(message);
                    const newRow = orderId + "," + ediOrderId + "," + "O" + "," + message;
                    orderResults.push(newRow);
                });
            }
            else {
                const message = "Order " + orderId + " created. Reply not required.";
                if (selectedRows.length == 1) {
                    Toast.showToast(this.getResultsToastPanel(orderId, 'Reply not required.', 'created'), null, { millisUntilDismissal: 7000 });
                }
                const newRow = orderId + "," + ediOrderId + "," + "O" + "," + message;
                orderResults.push(newRow);
            }
        }
        else {
            if (originalReplyReq == "Y" && replyCreated != "Y") {
                this.waitingOnInput = true;
                CommonDialogs.showYesNo("Order not created.  Would you still like to accept the tender?", "Reply to Tender",
                    { titleProps: { imageName: ImageName.circleX, imageColor: "primary.light" } }).then(async yes => {
                        if (yes) {
                            await this.acceptLoadTender(ediOrderId, orderId).then(response => {
                                if (response != null) {
                                    const batch = response.data[0].reply_batch;

                                    if (batch != "-1" && batch != "0") {
                                        const message = "Order not created.  Reply sent with batch " + batch + ".";
                                        if (selectedRows.length == 1) Toast.showSuccessToast(message);
                                        const newRow = "N/A" + "," + ediOrderId + "," + "O" + "," + message;
                                        orderResults.push(newRow);
                                        this.waitingOnInput = false;
                                    }
                                    else {
                                        const error = response.data[0].description;
                                        const message = "Order not created.  Unable to send reply.  " + error;
                                        Snackbar.showWarningSnackbar(message);
                                        const newRow = "N/A" + "," + ediOrderId + "," + "O" + "," + message;
                                        orderResults.push(newRow);
                                        this.waitingOnInput = false;
                                    }
                                }
                                else {
                                    const message = "Unable to send reply.";
                                    Snackbar.showWarningSnackbar(message);
                                    const newRow = "N/A" + "," + ediOrderId + "," + "O" + "," + message;
                                    orderResults.push(newRow);
                                    this.waitingOnInput = false;
                                }

                            });
                            this.buttonAccept.busy = false;
                        }
                        else {
                            if (selectedRows.length > 1)
                                this.disableReplyOptions();
                            else
                                this.enableReplyOptions();

                            const newRow = orderId + "," + ediOrderId + "," + "O" + "," + "Tender skipped.";
                            orderResults.push(newRow);
                            this.waitingOnInput = false;
                        }
                    }).catch(async () => {
                        this.waitingOnInput = false;
                        await this.setRecordInUse(ediOrderRow, "N").then(() => { this.readyForNext = true; });
                    });
            }
            else {
                Snackbar.showWarningSnackbar("Unable to create the order.");
            }
        }
    }

    sourceLoadTenderExpressOnRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        let eventChangeType = event.change.type;
        if (eventChangeType != ModelDataChangeType.DELETE && !this.filterObject.dataPasses(event.change.data))
            eventChangeType = ModelDataChangeType.DELETE;

        // Invalidate relevant caches when data changes
        const changedRow = event.change.data as ModelRow;
        const tenderId = changedRow.data["tender_id"];
        const orderId = changedRow.data["order_id"];

        // Clear caches for changed items
        if (tenderId) {
            this._ediOrderCache.delete(tenderId);
        }
        if (orderId) {
            this._orderStatusCache.delete(orderId);
        }

        const expandedRows: TableRow[] = [];
        let selectedLayout: TenderGridLayout;
        const setExpandedRows = (layout: TenderGridLayout) => {
            layout.tableLtx.rows.forEach(row => { if (row.expanded) expandedRows.push(row); });
            selectedLayout = layout;
        };
        const handleRemoteDataChange = (tab: Tab, layout: TenderGridLayout) => {
            layout.sourceLoadTenderExpressRemote.handleDataChange({ type: eventChangeType, data: changedRow });
            this.setCaption(tab, layout);
            layout.filterPanel.updateFilterCounts();
        }
        const currentCompany: string = UserSettings.get()?.company_id?.toLowerCase();
        const changedRowCompany: string = changedRow.get("company_id", "").toLowerCase();
        if (currentCompany !== changedRowCompany) {
            return;
        }
        const changedValues = changedRow.compareWithData(changedRow.get("originalData"));
        const isOriginal = changedRow.data["is_original"];
        const isChange = changedRow.data["is_change"];
        const isCancel = changedRow.data["is_cancel"];

        // Get original values to detect changes in purpose
        const originalData = changedRow.get("originalData");
        let wasOriginal = false;
        let wasChange = false;
        let wasCancel = false;

        if(originalData) {
            wasOriginal = originalData["is_original"];
            wasChange = originalData["is_change"];
            wasCancel = originalData["is_cancel"];
        }

        // Purpose has changed - remove from old tab
        if (wasOriginal && !isOriginal) {
            this.layoutOriginal.sourceLoadTenderExpressRemote.handleDataChange({
                type: ModelDataChangeType.DELETE, data: changedRow
            });
            this.setCaption(this.tabOriginal, this.layoutOriginal);
            this.layoutOriginal.filterPanel.updateFilterCounts();
        } else if (wasChange && !isChange) {
            this.layoutChange.sourceLoadTenderExpressRemote.handleDataChange({
                type: ModelDataChangeType.DELETE, data: changedRow
            });
            this.setCaption(this.tabChange, this.layoutChange);
            this.layoutChange.filterPanel.updateFilterCounts();
        } else if (wasCancel && !isCancel) {
            this.layoutCancel.sourceLoadTenderExpressRemote.handleDataChange({
                type: ModelDataChangeType.DELETE, data: changedRow
            });
            this.setCaption(this.tabCancel, this.layoutCancel);
            this.layoutCancel.filterPanel.updateFilterCounts();
        }

        setExpandedRows(this.layoutAll);
        setExpandedRows(this.layoutOriginal);
        setExpandedRows(this.layoutChange);
        setExpandedRows(this.layoutCancel);

        if (changedValues["ltx_status"] != undefined && !changedValues["ltx_status"]) {
            eventChangeType = ModelDataChangeType.DELETE;
        }

        // Update tabs with current data
        handleRemoteDataChange(this.tabAll, this.layoutAll);
        if (isOriginal) {
            handleRemoteDataChange(this.tabOriginal, this.layoutOriginal);
        } else if (isChange) {
            handleRemoteDataChange(this.tabChange, this.layoutChange);
        } else if (isCancel) {
            handleRemoteDataChange(this.tabCancel, this.layoutCancel);
        }

        this.mainDataSource.handleDataChange({ type: eventChangeType, data: changedRow });
        if (eventChangeType == ModelDataChangeType.UPDATE) {
            const existingRow: boolean = this.mainDataSource.data.some(row => row.get("tender_id") === tenderId);
            if (!existingRow) {
                this.mainDataSource.addRow(changedRow);
            }
        }

        // Ensure textbox captions are visible for newly added rows
        if (eventChangeType === ModelDataChangeType.ADD || eventChangeType === ModelDataChangeType.UPDATE) {
            // Use setTimeout to allow the DOM to update before applying textbox settings
            setTimeout(() => {
                this.ensureTextboxCaptionsForNewRows(tenderId);
            }, 50);
        }

        this.updateRefreshLabel();
        event.preventDefault();
        this.refreshExpandedComponents(selectedLayout, tenderId, expandedRows);
    }

    sourceLoadTenderExpressOnSubscriberError(event) {
        log.error(event.error);
    }

    sourceEdiStopOnSubscriberError(event) {
        log.error(event.error);
    }

    updateRefreshLabel() {
        this.labelLastRefreshDateTime.text = new Date().toLocaleString("en-US", dateTimeOptions).replace(":", "").replace(",", "");
    }

    refreshExpandedComponents(layout: TenderGridLayout, tenderIdInbound: string, expandedRows: TableRow[]) {
        expandedRows.forEach(async expandedRow => {
            const tenderId = expandedRow.data.get("tender_id");
            const row = layout.tableLtx.allRows.find(row => row.data.get("tender_id") === tenderId);
            if (row) {
                if (tenderId === tenderIdInbound) {
                    row.setExpandedAsync(true).then((expandComp: Panel) => {
                        const tableLtxStopGrid = expandComp.findComponentById("tableLtxStopGrid");
                        // Only search if data source is empty to avoid redundant queries
                        if (tableLtxStopGrid?.dataSource && tableLtxStopGrid.dataSource.data.length === 0) {
                            tableLtxStopGrid.dataSource.search({ order_id: tenderId });
                        }
                    });
                } else {
                    row.expanded = true;
                }
            }
        });
    }

    private setCaption(tab: Tab, layout: TenderGridLayout) {
        let firstWord: string;
        switch (tab.id) {
            case "tabOriginal":
                firstWord = "Original";
                break;
            case "tabChange":
                firstWord = "Change";
                break;
            case "tabCancel":
                firstWord = "Cancel";
                break;
            default:
                firstWord = "All"
                break;
        }
        tab.caption = `${firstWord} ${layout.mainDataSource.data.length}`;
    }

    private async setRecordInUse(ediOrderRow: RowEdiOrder, valueToSet: string) {
        const locked = ediOrderRow.get("record_in_use", "N");
        if (locked !== valueToSet) {
            ediOrderRow.set("record_in_use", valueToSet);
            await ediOrderRow.post();
        }
    }

    private async noDisplayTender(ediOrderRow: RowEdiOrder) {
        if (ediOrderRow.get("no_display") != "Y") {
            log.info("Excluding edi_order with id [" + ediOrderRow.getString("id") + "]");
            ediOrderRow.set("no_display", "Y");
            await ediOrderRow.post();
        }
    }

    async findOriginalOrderId(ediOrderRow): Promise<any> {
        return await EdiUtility.findOriginalOrderId(ediOrderRow);
    }

    /**
     * Ensures that textbox captions are visible for newly added rows by manually calling setupTextboxes
     * for the row with the specified tender ID across all tab layouts.
     */
    private ensureTextboxCaptionsForNewRows(tenderId: string) {
        const layouts = [this.layoutAll, this.layoutOriginal, this.layoutChange, this.layoutCancel];

        layouts.forEach(layout => {
            if (layout && layout.tableLtx && layout.tableLtx.rows) {
                const targetRow = layout.tableLtx.rows.find(row =>
                    row.data && row.data.get("tender_id") === tenderId
                );

                if (targetRow) {
                    try {
                        // Call setupTextboxes directly on the TenderGridLayout
                        (layout as TenderGridLayout).setupTextboxes(targetRow);
                    } catch (error) {
                        log.debug(`Error setting up textboxes for tender ${tenderId}:`, error);
                        // Retry after a longer delay if components aren't ready
                        setTimeout(() => {
                            try {
                                (layout as TenderGridLayout).setupTextboxes(targetRow);
                            } catch (retryError) {
                                log.debug(`Retry failed for tender ${tenderId}:`, retryError);
                            }
                        }, 100);
                    }
                }
            }
        });
    }
}
